import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function AdminPromos() {
  const [promos, setPromos] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_pct: "", max_uses: "100", active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch(() => setPromos(false));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.code || !form.discount_pct) {
      toast.error("Code and discount are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/promo-codes", {
        code: form.code,
        discount_pct: Number(form.discount_pct),
        max_uses: Number(form.max_uses) || 100,
        active: form.active,
      });
      setPromos((p) => [data, ...p]);
      setOpen(false);
      setForm({ code: "", discount_pct: "", max_uses: "100", active: true });
      toast.success("Promo code created");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    try {
      const { data } = await api.put(`/admin/promo-codes/${p.id}/toggle`);
      setPromos((list) => list.map((x) => (x.id === p.id ? { ...x, active: data.active } : x)));
    } catch (e) {
      toast.error("Failed to toggle");
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/promo-codes/${p.id}`);
      setPromos((list) => list.filter((x) => x.id !== p.id));
      toast.success("Promo deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (promos === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (promos === false) return <p className="text-sm text-destructive">Failed to load promo codes.</p>;

  return (
    <div data-testid="admin-promos">
      <PageHeader
        title="Promo Codes"
        subtitle="Discount codes for new subscriptions"
        action={
          <Button onClick={() => setOpen(true)} className="gap-2 font-semibold" data-testid="add-promo-button">
            <Plus className="h-4 w-4" /> New code
          </Button>
        }
      />

      <div className="rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promos.map((p) => (
              <TableRow key={p.id} data-testid={`promo-row-${p.id}`}>
                <TableCell className="font-mono font-semibold">{p.code}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{p.discount_pct}% off</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{p.uses} / {p.max_uses}</TableCell>
                <TableCell>
                  <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 text-destructive hover:text-destructive"
                    onClick={() => remove(p)}
                    data-testid={`promo-delete-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New promo code</DialogTitle>
            <DialogDescription>Create a discount code customers can apply at checkout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="SUMMER25"
                className="h-11 font-mono"
                data-testid="promo-code-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="disc">Discount %</Label>
                <Input
                  id="disc"
                  type="number"
                  value={form.discount_pct}
                  onChange={(e) => setForm((f) => ({ ...f, discount_pct: e.target.value }))}
                  placeholder="25"
                  className="h-11"
                  data-testid="promo-discount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max">Max uses</Label>
                <Input
                  id="max"
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
                  className="h-11"
                  data-testid="promo-maxuses-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
              <span className="text-sm font-medium">Active immediately</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="font-semibold" data-testid="promo-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
